// Notification Service for LEMO
// Uses the Browser Notifications API

const NOTIFICATION_PERMISSION_KEY = 'lemo_notifications_enabled';

// Check if notifications are supported
export const isNotificationSupported = () => {
  return 'Notification' in window;
};

// Get current permission status
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

// Check if notifications are enabled by user preference
export const areNotificationsEnabled = () => {
  const enabled = localStorage.getItem(NOTIFICATION_PERMISSION_KEY);
  return enabled === 'true' && getNotificationPermission() === 'granted';
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    return { success: false, reason: 'unsupported' };
  }

  if (Notification.permission === 'granted') {
    localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'true');
    return { success: true, permission: 'granted' };
  }

  if (Notification.permission === 'denied') {
    return { success: false, reason: 'denied' };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'true');
      return { success: true, permission: 'granted' };
    }
    return { success: false, reason: permission };
  } catch (error) {
    return { success: false, reason: 'error' };
  }
};

// Disable notifications
export const disableNotifications = () => {
  localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'false');
};

// Enable notifications (if permission already granted)
export const enableNotifications = () => {
  if (getNotificationPermission() === 'granted') {
    localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'true');
    return true;
  }
  return false;
};

// Show a notification
export const showNotification = (title, options = {}) => {
  if (!areNotificationsEnabled()) return null;

  const defaultOptions = {
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [200, 100, 200],
    requireInteraction: false,
    silent: false,
    ...options
  };

  try {
    const notification = new Notification(title, defaultOptions);
    
    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);
    
    // Handle click
    notification.onclick = () => {
      window.focus();
      if (options.onClick) options.onClick();
      notification.close();
    };

    return notification;
  } catch (error) {
    console.error('Failed to show notification:', error);
    return null;
  }
};

// Order status notification messages
const ORDER_STATUS_NOTIFICATIONS = {
  'En préparation': {
    title: '👨‍🍳 Commande en préparation',
    body: (orderNumber, restaurant) => `Votre commande ${orderNumber} est en cours de préparation chez ${restaurant}`,
    tag: 'order-preparing'
  },
  'Prête': {
    title: '✅ Commande prête !',
    body: (orderNumber, restaurant) => `Votre commande ${orderNumber} vous attend chez ${restaurant}. Venez la récupérer !`,
    tag: 'order-ready',
    requireInteraction: true
  },
  'Terminée': {
    title: '🎉 Commande terminée',
    body: (orderNumber) => `Merci pour votre commande ${orderNumber} ! N'hésitez pas à laisser un avis.`,
    tag: 'order-completed'
  },
  'Annulée': {
    title: '😔 Commande annulée',
    body: (orderNumber) => `Votre commande ${orderNumber} a été annulée.`,
    tag: 'order-cancelled'
  }
};

// Show order status notification
export const showOrderStatusNotification = (order, newStatus, onClick) => {
  const config = ORDER_STATUS_NOTIFICATIONS[newStatus];
  if (!config) return null;

  return showNotification(config.title, {
    body: config.body(order.order_number, order.restaurant_name),
    tag: `${config.tag}-${order.id}`,
    requireInteraction: config.requireInteraction || false,
    data: { orderId: order.id },
    onClick
  });
};

// Show new order notification for restaurants
export const showNewOrderNotification = (order, onClick) => {
  // Ne pas afficher si l'onglet est visible
  if (document.visibilityState === 'visible') return null;
  
  return showNotification('🔔 Nouvelle commande !', {
    body: `${order.order_number} - ${order.total_amount?.toFixed(2) || '0.00'}€`,
    tag: 'new-order',
    renotify: true,
    requireInteraction: true,
    onClick
  });
};

// Show multiple new orders notification for restaurants
export const showMultipleOrdersNotification = (count, onClick) => {
  // Ne pas afficher si l'onglet est visible
  if (document.visibilityState === 'visible') return null;
  
  return showNotification('🔔 Nouvelles commandes !', {
    body: `${count} nouvelle(s) commande(s) en attente`,
    tag: 'new-orders',
    renotify: true,
    requireInteraction: true,
    onClick
  });
};

// Play notification sound
export const playNotificationSound = () => {
  try {
    const audio = new Audio('/notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {
      // Autoplay blocked by browser policy - non-critical
    });
  } catch {
    // Sound playback not available on this device
  }
};
